/**
 * @module qbLinkage/detectChanges/fieldDiffUtils
 * @description 维/指列表结构化 diff 工具
 */

import type { IFieldSnapshot } from '../types';

export function findReorder(
  prev: IFieldSnapshot[],
  next: IFieldSnapshot[],
): { oldIndex: number; newIndex: number; field: IFieldSnapshot } | undefined {
  if (prev.length !== next.length) {
    return undefined;
  }
  const prevIds = prev.map(f => f.id);
  const nextIds = next.map(f => f.id);
  const sameSet =
    prevIds.length === nextIds.length &&
    prevIds.every(id => nextIds.includes(id));
  if (!sameSet) {
    return undefined;
  }
  const orderChanged = prevIds.some((id, i) => id !== nextIds[i]);
  if (!orderChanged) {
    return undefined;
  }
  for (let i = 0; i < next.length; i += 1) {
    if (prevIds[i] !== nextIds[i]) {
      const movedId = nextIds[i];
      const oldIndex = prevIds.indexOf(movedId);
      const field = next.find(f => f.id === movedId);
      if (field && oldIndex >= 0) {
        return { oldIndex, newIndex: i, field };
      }
    }
  }
  return undefined;
}

export function diffRenames(
  prev: IFieldSnapshot[],
  next: IFieldSnapshot[],
): Array<{
  field: IFieldSnapshot;
  prevColumnName: string;
  nextColumnName: string;
  displayOnly: boolean;
}> {
  const renamed: Array<{
    field: IFieldSnapshot;
    prevColumnName: string;
    nextColumnName: string;
    displayOnly: boolean;
  }> = [];
  const nextById = new Map(next.map(f => [f.id, f]));
  for (const p of prev) {
    const n = nextById.get(p.id);
    if (!n) {
      continue;
    }
    const nameChanged = p.column_name !== n.column_name;
    const displayChanged =
      p.column_display !== n.column_display ||
      p.column_display_en !== n.column_display_en;
    if (!nameChanged && !displayChanged) {
      continue;
    }
    renamed.push({
      field: n,
      prevColumnName: p.column_name,
      nextColumnName: n.column_name,
      displayOnly: !nameChanged,
    });
  }
  return renamed;
}

export function removedFields(
  prev: IFieldSnapshot[],
  next: IFieldSnapshot[],
): IFieldSnapshot[] {
  const nextIds = new Set(next.map(f => f.id));
  return prev.filter(f => !nextIds.has(f.id));
}

export function addedFields(
  prev: IFieldSnapshot[],
  next: IFieldSnapshot[],
): IFieldSnapshot[] {
  const prevIds = new Set(prev.map(f => f.id));
  return next.filter(f => !prevIds.has(f.id));
}
